/**
 * Public /projects/:slug — long-form write-up of one project.
 * Renders the admin-authored markdown body via the shared md renderer.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Github, ExternalLink, Rocket, Wrench, Calendar, Globe } from "lucide-react";
import SiteHeader from "@/components/marketplace/SiteHeader";
import SiteFooter from "@/components/marketplace/SiteFooter";
import BullseyeEmbed from "@/components/marketplace/BullseyeEmbed";
import Seo from "@/components/Seo";
import { fetchProject, trackProjectView } from "@/lib/api";
import { renderParagraphs } from "@/lib/md";

// Stable per-browser visitor id for view-count deduplication. LocalStorage
// value; never sent anywhere except our own /track endpoint. Falls back to
// a random ephemeral id when localStorage is blocked (private windows).
function getVisitorId() {
  try {
    const key = "cd_visitor_id";
    let v = localStorage.getItem(key);
    if (!v) {
      v = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(key, v);
    }
    return v;
  } catch {
    return `eph-${Math.random().toString(36).slice(2, 10)}`;
  }
}

export default function ProjectDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setP(null); setNotFound(false);
    fetchProject(slug).then((data) => {
      setP(data);
      // Fire-and-forget view tracker. 30-min dedup on the backend so a
      // reader refreshing the page doesn't inflate the count.
      trackProjectView(slug, {
        visitor_id: getVisitorId(),
        referrer: document.referrer || "",
      });
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <>
        <SiteHeader />
        <main className="cd-container" style={{ padding: "120px 0", textAlign: "center", fontFamily: "var(--cd-font)" }}>
          <h1 className="cd-display" data-testid="project-notfound-title">Project not found</h1>
          <p style={{ color: "var(--cd-gray-500)", marginTop: 12 }}>
            The write-up you're looking for was unpublished or renamed.
          </p>
          <Link to="/projects" className="cd-btn cd-btn--outline" style={{ marginTop: 30, display: "inline-flex" }}>
            <ArrowLeft size={14} /> Back to all projects
          </Link>
        </main>
        <SiteFooter />
      </>
    );
  }

  if (!p) {
    return (
      <>
        <SiteHeader />
        <main className="cd-container" style={{ padding: "120px 0", textAlign: "center", fontFamily: "var(--cd-font)", color: "var(--cd-gray-500)" }}>
          Loading…
        </main>
      </>
    );
  }

  const isFinished = p.status === "finished";
  const sections = splitSections(p.body_md || "");

  return (
    <>
      <Seo
        title={`${p.title} · Projects · CasperDomains`}
        description={p.summary || `${p.title} — a case study from the CasperDomains desk.`}
        type="article"
        siteName="CasperDomains"
        image={p.cover_url}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: p.title,
          description: p.summary,
          image: p.cover_url,
          datePublished: p.created_at,
          dateModified: p.updated_at || p.created_at,
          author: { "@type": "Organization", name: "CasperDomains" },
        }}
      />
      <SiteHeader />
      <main
        style={{ fontFamily: "var(--cd-font)", background: "var(--cd-white)", paddingBottom: 120 }}
        data-testid="project-detail-page"
      >
        {/* Hero band */}
        <div
          style={{
            background: "linear-gradient(135deg, #03142f, #071d40)",
            color: "#fff",
            paddingTop: 60, paddingBottom: 80,
            position: "relative", overflow: "hidden",
          }}
        >
          <div className="cd-container" style={{ position: "relative", zIndex: 1, maxWidth: 820 }}>
            <Link
              to="/projects"
              data-testid="project-back"
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                color: "rgba(255,255,255,.7)", fontSize: 13, fontWeight: 600,
                textDecoration: "none", marginBottom: 22,
              }}
            >
              <ArrowLeft size={14} /> All projects
            </Link>

            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
              <span
                className="cd-projects__status-chip"
                data-testid="project-status-chip"
                style={{
                  background: isFinished ? "rgba(62,232,211,.15)" : "rgba(255,255,255,.08)",
                  color: isFinished ? "var(--cd-cyan-400)" : "#fff",
                  border: `1px solid ${isFinished ? "rgba(62,232,211,.35)" : "rgba(255,255,255,.2)"}`,
                }}
              >
                {isFinished ? <Rocket size={11} strokeWidth={2.4} /> : <Wrench size={11} strokeWidth={2.4} />}
                {isFinished ? "Shipped" : "In flight"}
              </span>
              {p.published_at && (
                <span style={{ fontSize: 12, color: "rgba(255,255,255,.5)", display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Calendar size={11} strokeWidth={2} />
                  {new Date(p.published_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                </span>
              )}
            </div>

            <h1
              className="cd-display"
              data-testid="project-title"
              style={{ color: "#fff", letterSpacing: "-0.03em", margin: 0, fontSize: "clamp(38px, 5vw, 62px)" }}
            >
              {p.title}
            </h1>

            {p.summary && (
              <p style={{ marginTop: 22, fontSize: 20, lineHeight: 1.55, color: "rgba(255,255,255,.75)" }}>
                {p.summary}
              </p>
            )}

            {(p.demo_url || p.github_url) && (
              <div style={{ display: "flex", gap: 12, marginTop: 30, flexWrap: "wrap" }} data-testid="project-links">
                {p.demo_url && (
                  <a
                    href={p.demo_url}
                    target="_blank"
                    rel="noreferrer"
                    className="cd-btn cd-btn--primary cd-btn--sm"
                    data-testid="project-demo-link"
                  >
                    <ExternalLink size={14} /> Live demo
                  </a>
                )}
                {p.github_url && (
                  <a
                    href={p.github_url}
                    target="_blank"
                    rel="noreferrer"
                    className="cd-btn cd-btn--outline cd-btn--sm"
                    style={{ color: "#fff", borderColor: "rgba(255,255,255,.3)", background: "transparent" }}
                    data-testid="project-github-link"
                  >
                    <Github size={14} /> Source
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Cover image */}
        {p.cover_url && (
          <div className="cd-container" style={{ marginTop: -40, position: "relative", zIndex: 2 }}>
            <img
              src={p.cover_url}
              alt=""
              data-testid="project-cover"
              style={{
                width: "100%", maxWidth: 960, margin: "0 auto",
                display: "block", borderRadius: 24,
                boxShadow: "0 40px 90px -20px rgba(3,10,30,.35)",
                aspectRatio: "16 / 9", objectFit: "cover",
                objectPosition: p.cover_focal
                  ? `${p.cover_focal.x}% ${p.cover_focal.y}%`
                  : "center center",
              }}
            />
          </div>
        )}

        {/* Body — long-form markdown */}
        <article
          className="cd-container"
          style={{ maxWidth: 720, marginTop: 72, fontSize: 17, lineHeight: 1.75, color: "var(--cd-navy-950)" }}
          data-testid="project-body"
        >
          {sections.map((s, i) => (
            <section key={i} style={{ marginTop: i > 0 ? 42 : 0 }}>
              {s.title && (
                <h2 style={{ fontSize: 26, fontWeight: 800, letterSpacing: "-0.02em", margin: "0 0 14px", color: "var(--cd-navy-950)" }}>
                  {s.title}
                </h2>
              )}
              {renderParagraphs(s.body, `s${i}`)}
            </section>
          ))}

          {p.linked_domains?.length > 0 && (
            <div style={{ marginTop: 60, paddingTop: 30, borderTop: "1px solid var(--cd-gray-200)" }} data-testid="project-linked-domains">
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--cd-gray-500)", marginBottom: 14 }}>
                Domains this shipped for
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {p.linked_domains.map((slug) => (
                  <Link
                    key={slug}
                    to={`/l/${slug}`}
                    data-testid={`linked-domain-${slug}`}
                    style={{
                      padding: "10px 16px", borderRadius: 12,
                      background: "linear-gradient(135deg, #03142f, #0a2456 65%, #4b6fff)",
                      color: "#fff",
                      textDecoration: "none",
                      fontFamily: "var(--cd-font)",
                      fontWeight: 700, fontSize: 15, letterSpacing: "-0.01em",
                      display: "inline-flex", alignItems: "center", gap: 8,
                      transition: "transform .18s ease",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
                  >
                    <Globe size={13} strokeWidth={2.2} style={{ opacity: .8 }} />
                    {slug}
                    <ExternalLink size={12} strokeWidth={2.2} style={{ opacity: .6 }} />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {p.tech_tags?.length > 0 && (
            <div style={{ marginTop: 60, paddingTop: 30, borderTop: "1px solid var(--cd-gray-200)" }}>
              <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "var(--cd-gray-500)", marginBottom: 14 }}>
                Stack
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }} data-testid="project-tags">
                {p.tech_tags.map((t) => (
                  <span
                    key={t}
                    style={{
                      padding: "6px 12px", borderRadius: 999,
                      background: "var(--cd-gray-100, #f4f4f5)",
                      color: "var(--cd-navy-950)",
                      fontSize: 13, fontWeight: 600,
                    }}
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          )}
        </article>
      </main>
      <SiteFooter />
      <BullseyeEmbed page="projects" />
    </>
  );
}

function splitSections(src) {
  const parts = src.replace(/\r\n?/g, "\n").trim().split(/^##\s+(.+)$/m);
  const lede = (parts.shift() || "").trim();
  const out = lede ? [{ title: null, body: lede }] : [];
  for (let i = 0; i < parts.length; i += 2) {
    out.push({ title: (parts[i] || "").trim(), body: (parts[i + 1] || "").trim() });
  }
  return out;
}
